import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { MessageCircle, X, Send, Loader2, Bot, User, Sparkles, BookOpen } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useLibraryStore } from '../../stores/libraryStore';
import { useUserStore } from '../../stores/userStore';
import { aiApi } from '../../services/api';
import { UpgradeModal, useUpgradeCheck, LimitType } from './upgrade-modal';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';

type ChatSource = { materialId?: string; filename?: string; excerpt?: string; confidence?: number } | string;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
  timestamp: Date;
}

interface ChatPanelProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function ChatPanel({ isOpen, onToggle }: ChatPanelProps) {
  const { t: translate, tList } = useI18n();
  const translatorRef = useRef(translate);
  translatorRef.current = translate;
  // Keep delayed feedback in the current language without restarting the request.
  const t = useCallback<typeof translate>((key, values) => translatorRef.current(key, values), []);
  const { getToken } = useAuth();
  const { selectedMaterial } = useLibraryStore();
  const { fetchLimits } = useUserStore();
  const { isAtLimit } = useUpgradeCheck();
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Clear messages when document changes
  useEffect(() => {
    setMessages([]);
  }, [selectedMaterial?.id]);

  const handleSend = useCallback(async () => {
    const query = inputValue.trim();
    if (!query || isLoading) return;

    if (!selectedMaterial) {
      toast.error(t('chat.toast.selectDocument'));
      return;
    }

    // Proactive limit check
    if (isAtLimit('ask_ai')) {
      setUpgradeModalOpen(true);
      return;
    }

    const token = await getToken();
    if (!token) {
      toast.error(t('chat.toast.signIn'));
      return;
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: query,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await aiApi.qa(selectedMaterial.id, query, token);

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.answer,
        sources: response.sources,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      // Refresh limits after usage
      fetchLimits(token);
    } catch (error: any) {
      console.error('Q&A error:', error);
      
      // Check if it's a limit error from the backend
      if (error?.status === 403 || error?.message?.includes('limit') || error?.message?.includes('Free tier')) {
        setUpgradeModalOpen(true);
      } else {
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: t('chat.errorReply'),
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, selectedMaterial, getToken, isAtLimit, fetchLimits, t]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = tList('chat.suggested');

  return (
    <>
      {/* Floating Chat Button — desktop only (mobile uses bottom nav) */}
      {!isOpen && (
        <DraggableAskAIButton onToggle={onToggle} label={t('chat.title')} />
      )}

      {/* Chat Panel */}
      <div
        className={`fixed top-0 end-0 h-full z-50 transition-all duration-300 ease-in-out ${
          isOpen ? 'w-full sm:w-[420px] translate-x-0' : 'w-0 translate-x-full rtl:-translate-x-full'
        }`}
      >
        <div className="flex h-full min-h-0 flex-col bg-white/95 backdrop-blur-xl border-s border-white/20 shadow-2xl">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#7367FF]/5 to-[#2DD2FE]/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center shadow-md">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#434343]">{t('chat.title')}</h3>
                <p className="text-xs text-[#434343]/60 truncate max-w-[250px]">
                  {selectedMaterial
                    ? selectedMaterial.original_filename || selectedMaterial.filename
                    : t('chat.noDocument')}
                </p>
              </div>
            </div>
            <button
              aria-label={t("common.close")}
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-4 w-4 text-[#434343]" />
            </button>
          </div>

          {/* Messages Area — min-h-0 so flex-1 can actually scroll */}
          <ScrollArea className="min-h-0 flex-1 px-4 py-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center px-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 flex items-center justify-center mb-4">
                  <BookOpen className="h-8 w-8 text-[#7367FF]" />
                </div>
                <h4 className="text-lg font-semibold text-[#434343] mb-2">
                  {t('chat.emptyTitle')}
                </h4>
                <p className="text-sm text-[#434343]/60 mb-6 max-w-[280px]">
                  {t('chat.emptyBody')}
                </p>

                {/* Suggested Questions */}
                <div className="space-y-2 w-full max-w-[300px]">
                  <p className="text-xs text-[#434343]/40 uppercase tracking-wider">{t('chat.tryAsking')}</p>
                  {suggestedQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputValue(question);
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                      className="w-full text-start px-3 py-2 rounded-lg border border-gray-100 text-sm text-[#434343]/70 hover:border-[#7367FF]/30 hover:bg-[#7367FF]/5 hover:text-[#7367FF] transition-all"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white'
                          : 'bg-gray-50 border border-gray-100 text-[#434343]'
                      }`}
                    >
                      <p dir="auto" className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200/50">
                          <p className="text-xs text-[#434343]/50 mb-1">{t('chat.sources')}</p>
                          {message.sources.map((source, idx) => (
                            <p key={idx} dir="auto" className="text-xs text-[#7367FF]/70 truncate">
                              {typeof source === 'string' ? source : source.filename || source.excerpt || t('common.document')}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="w-7 h-7 rounded-lg bg-[#434343] flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="h-3.5 w-3.5 text-white" />
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-[#7367FF]" />
                        <span className="text-sm text-[#434343]/60">{t('chat.thinking')}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="shrink-0 border-t border-gray-100 p-4 bg-white/80">
            <div className="flex items-end gap-2">
              <textarea
                dir="auto"
                aria-label={t("chat.inputPlaceholder")}
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  selectedMaterial ? t('chat.inputPlaceholder') : t('chat.inputPlaceholderNoDoc')
                }
                disabled={!selectedMaterial || isLoading}
                rows={1}
                className="h-11 min-h-11 max-h-11 flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm leading-[1.35rem] text-[#434343] placeholder:text-[#434343]/40 focus:border-[#7367FF] focus:outline-none focus:ring-2 focus:ring-[#7367FF]/20 disabled:cursor-not-allowed disabled:opacity-50 transition-all py-[0.625rem]"
              />
              <Button
                size="sm"
                aria-label={t("chat.sendMessage")}
                onClick={handleSend}
                disabled={!inputValue.trim() || !selectedMaterial || isLoading}
                className="h-11 w-11 shrink-0 p-0 rounded-xl bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 disabled:opacity-30 transition-all"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                ) : (
                  <Send className="h-4 w-4 text-white rtl-flip" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-[#434343]/40 mt-2 text-center">
              {t('chat.disclaimer')}
            </p>
          </div>
        </div>
      </div>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 sm:hidden"
          onClick={onToggle}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        limitType="ask_ai"
      />
    </>
  );
}

function DraggableAskAIButton({ onToggle, label }: { onToggle: () => void; label: string }) {
  const didDrag = useRef(false);

  return (
    <motion.button
      drag
      dragMomentum={false}
      dragElastic={0}
      onDragStart={() => { didDrag.current = true; }}
      onDragEnd={() => { setTimeout(() => { didDrag.current = false; }, 0); }}
      onClick={() => { if (!didDrag.current) onToggle(); }}
      className="hidden md:flex fixed z-[55] items-center gap-2 px-4 py-3 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white rounded-full shadow-lg shadow-[#7367FF]/30 hover:shadow-xl hover:shadow-[#7367FF]/40 cursor-grab active:cursor-grabbing"
      style={{ bottom: 32, right: 'calc(50% - 60px)' }}
      whileHover={{ scale: 1.05 }}
    >
      <MessageCircle className="h-5 w-5" />
      <span className="text-sm font-medium">{label}</span>
    </motion.button>
  );
}
